import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // axios interceptor: attach Bearer token automatically
  useEffect(() => {
    const id = axios.interceptors.request.use((config) => {
      const t = localStorage.getItem("dmu_token");
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, []);

  const fetchMe = useCallback(async () => {
    const t = localStorage.getItem("dmu_token");
    if (!t) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      localStorage.removeItem("dmu_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const loginWeb = async (usuario, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { usuario, password });
    localStorage.setItem("dmu_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  /* Variante usada por LoginModulosGrid: NO persiste sesión si el predicado falla.
     Si `acceptRol(user)` retorna false, se rechaza con error y no se guarda token/user. */
  const verifyAndLogin = async (usuario, password, acceptRol) => {
    const { data } = await axios.post(`${API}/auth/login`, { usuario, password });
    if (!acceptRol(data.user)) {
      const err = new Error("ROL_NO_AUTORIZADO");
      err.code = "ROL_NO_AUTORIZADO";
      throw err;
    }
    localStorage.setItem("dmu_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const loginChofer = async (apellido, password) => {
    const { data } = await axios.post(`${API}/auth/login-chofer`, { apellido, password });
    localStorage.setItem("dmu_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const loginLiderVial = async (n_empleado, n_unidad) => {
    const { data } = await axios.post(`${API}/auth/login-lider-vial`, { n_empleado, n_unidad });
    localStorage.setItem("dmu_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    localStorage.removeItem("dmu_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWeb, verifyAndLogin, loginChofer, loginLiderVial, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
